import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { availabilityService } from '../../services/availabilityService';

type DayOfWeek =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

interface Availability {
  id: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AvailabilityManagementSidebarProps {
  open: boolean;
  onClose: () => void;
  availability: Availability[];
}

interface AvailabilityFormData {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

const DAYS_OF_WEEK = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const TIME_SLOTS = [
  '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30',
  '22:00', '22:30', '23:00', '23:30',
];

const AvailabilityManagementSidebar: React.FC<AvailabilityManagementSidebarProps> = ({
  open,
  onClose,
  availability,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAvailabilityDialog, setShowAvailabilityDialog] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState<Availability | null>(null);
  const [draftAvailability, setDraftAvailability] = useState<Availability[]>(availability);
  const [availabilityForm, setAvailabilityForm] = useState<AvailabilityFormData>({
    dayOfWeek: 'MONDAY',
    startTime: '09:00',
    endTime: '17:00',
    isAvailable: true,
  });

  useEffect(() => {
    setDraftAvailability(availability);
  }, [availability, open]);

  const handleAddAvailability = () => {
    setEditingAvailability(null);
    setAvailabilityForm({
      dayOfWeek: 'MONDAY',
      startTime: '09:00',
      endTime: '17:00',
      isAvailable: true,
    });
    setShowAvailabilityDialog(true);
  };

  const handleEditAvailability = (availability: Availability) => {
    setEditingAvailability(availability);
    setAvailabilityForm({
      dayOfWeek: availability.dayOfWeek,
      startTime: availability.startTime,
      endTime: availability.endTime,
      isAvailable: availability.isAvailable,
    });
    setShowAvailabilityDialog(true);
  };

  const handleDeleteAvailability = async (availabilityId: string) => {
    if (!window.confirm('Are you sure you want to delete this availability slot?')) {
      return;
    }

    setDraftAvailability((prev) => prev.filter((slot) => slot.id !== availabilityId));
    setSuccess('Slot removed from draft. Save schedule to apply changes.');
  };

  const handleSaveAvailability = () => {
    if (editingAvailability) {
      setDraftAvailability((prev) =>
        prev.map((slot) =>
          slot.id === editingAvailability.id
            ? { ...slot, ...availabilityForm }
            : slot
        )
      );
      setSuccess('Slot updated in draft. Save schedule to apply changes.');
    } else {
      setDraftAvailability((prev) => [
        ...prev,
        {
          id: `draft-${Date.now()}`,
          dayOfWeek: availabilityForm.dayOfWeek,
          startTime: availabilityForm.startTime,
          endTime: availabilityForm.endTime,
          isAvailable: availabilityForm.isAvailable,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ]);
      setSuccess('Slot added to draft. Save schedule to apply changes.');
    }

    setShowAvailabilityDialog(false);
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleAvailabilityFormChange = (field: keyof AvailabilityFormData, value: string | boolean) => {
    setAvailabilityForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatDayOfWeek = (day: string) => {
    return day.charAt(0) + day.slice(1).toLowerCase();
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const isValid = availabilityForm.startTime < availabilityForm.endTime;
  const toDayOfWeek = (value: string): DayOfWeek => {
    if ((DAYS_OF_WEEK as string[]).includes(value)) {
      return value as DayOfWeek;
    }

    return 'MONDAY';
  };

  const handlePersistAvailability = async () => {
    try {
      setLoading(true);
      setError(null);

      await availabilityService.upsertMyWindows(
        draftAvailability.map((slot) => ({
          dayOfWeek: toDayOfWeek(slot.dayOfWeek),
          startTime: slot.startTime,
          endTime: slot.endTime,
          isAvailable: slot.isAvailable,
        }))
      );

      setSuccess('Availability schedule saved');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save availability schedule');
    } finally {
      setLoading(false);
    }
  };

  // Group availability by day of week
  const groupedAvailability = draftAvailability.reduce((acc, slot) => {
    if (!acc[slot.dayOfWeek]) {
      acc[slot.dayOfWeek] = [];
    }
    acc[slot.dayOfWeek].push(slot);
    return acc;
  }, {} as Record<string, Availability[]>);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : '40%',
            maxWidth: 600,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Set Availability</Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Manage your weekly availability schedule for patient appointments.
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddAvailability}
              fullWidth
              sx={{ mb: 3 }}
            >
              Add Time Slot
            </Button>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handlePersistAvailability}
              fullWidth
              disabled={loading || draftAvailability.length === 0}
              sx={{ mb: 3 }}
            >
              Save Schedule
            </Button>

            {draftAvailability.length > 0 ? (
              <Box>
                {DAYS_OF_WEEK.map((day) => {
                  const daySlots = groupedAvailability[day] || [];
                  if (daySlots.length === 0) return null;

                  return (
                    <Box key={day} sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {formatDayOfWeek(day)}
                      </Typography>
                      <List sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        {daySlots.map((slot) => (
                          <ListItem key={slot.id}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body1">
                                    {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                                  </Typography>
                                  {!slot.isAvailable && (
                                    <Chip label="Unavailable" size="small" color="default" />
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditAvailability(slot)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteAvailability(slot.id)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No availability set yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click "Add Time Slot" to set your availability
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Availability Form Dialog */}
      <Dialog
        open={showAvailabilityDialog}
        onClose={() => setShowAvailabilityDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingAvailability ? 'Edit Availability' : 'Add Time Slot'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={availabilityForm.dayOfWeek}
                onChange={(e) => handleAvailabilityFormChange('dayOfWeek', e.target.value)}
                label="Day of Week"
              >
                {DAYS_OF_WEEK.map((day) => (
                  <MenuItem key={day} value={day}>
                    {formatDayOfWeek(day)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Start Time</InputLabel>
                  <Select
                    value={availabilityForm.startTime}
                    onChange={(e) => handleAvailabilityFormChange('startTime', e.target.value)}
                    label="Start Time"
                  >
                    {TIME_SLOTS.map((time) => (
                      <MenuItem key={time} value={time}>
                        {formatTime(time)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>End Time</InputLabel>
                  <Select
                    value={availabilityForm.endTime}
                    onChange={(e) => handleAvailabilityFormChange('endTime', e.target.value)}
                    label="End Time"
                  >
                    {TIME_SLOTS.map((time) => (
                      <MenuItem key={time} value={time}>
                        {formatTime(time)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={availabilityForm.isAvailable}
                  onChange={(e) => handleAvailabilityFormChange('isAvailable', e.target.checked)}
                />
              }
              label="Available for appointments"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAvailabilityDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSaveAvailability}
            disabled={loading || !isValid}
          >
            {loading ? 'Saving...' : editingAvailability ? 'Update Availability' : 'Add Availability'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AvailabilityManagementSidebar;
